/**
 * Void Retaliation — foreshadow effects + orb launch from the void surface.
 *
 * Runs pre-switch in updatePhase() (not inside any phase case).
 * When a projectile hits the void, there's a chance the void fires back
 * with mortar orbs after a delay.
 */

import type { GameState } from '../types';
import type { GameEffect } from './effects';
import { transitionPhase } from './effects';
import { VOID_CONSUME, VOID_PERSONALITY } from '../constants';
import { MORTAR } from '../weapons';
import { random } from '../terrain/rng';
import { getActiveTheme } from '../stages/registry';
import { getRetaliationLine } from './void-personality';

/** Process queued void retaliation orbs — foreshadow effects, then simultaneous fan launch. */
export function processVoidRetaliation(state: GameState): GameEffect[] {
  const fx: GameEffect[] = [];

  if (state.voidRetaliationQueue.length === 0) {
    state.voidRetaliationActive = false;
    return fx;
  }

  const now = state.frameCount;
  const voidColor = getActiveTheme(state.stageId).voidEdgeColor;

  // Process foreshadow effects
  for (const orb of state.voidRetaliationQueue) {
    if (!orb.foreshadowed && now >= orb.foreshadowTime) {
      orb.foreshadowed = true;
      fx.push({ type: 'particles', method: 'spawnVoidForeshadow', args: [orb.x, state.voidY, voidColor] });
      fx.push({ type: 'void_foreshadow', x: orb.x, y: state.voidY });
    }
  }

  // Check if all orbs have been launched and resolved — must run before early returns
  const allDone = state.voidRetaliationQueue.every(o => o.launched);
  if (allDone && state.projectiles.length === 0) {
    state.voidRetaliationQueue = [];
    state.voidRetaliationActive = false;
    return fx;
  }

  // Batch-aware launch: collect ALL orbs whose launchTime <= now, launch them simultaneously
  if (state.projectiles.length > 0) return fx;

  const readyOrbs = state.voidRetaliationQueue.filter(o => !o.launched && now >= o.launchTime);
  if (readyOrbs.length === 0) return fx;

  // Mark all ready orbs as launched
  for (const orb of readyOrbs) orb.launched = true;

  const count = readyOrbs.length;
  const halfArc = VOID_PERSONALITY.FAN_ARC / 2;
  const straightUp = -Math.PI / 2;

  // Launch all orbs in a fan pattern
  for (let i = 0; i < count; i++) {
    const orb = readyOrbs[i];

    // Fan angle: single orb = straight up, multiple = evenly spread across arc
    let angle = straightUp;
    if (count > 1) {
      angle = straightUp - halfArc + (i / (count - 1)) * VOID_PERSONALITY.FAN_ARC;
    }

    const speed = Math.abs(VOID_PERSONALITY.FAN_SPEED);
    const vx = Math.cos(angle) * speed + (random() - 0.5) * VOID_PERSONALITY.FAN_VX_JITTER * 2;
    const vy = Math.sin(angle) * speed;

    const voidProj = {
      id: state.nextProjectileId++,
      x: orb.x,
      y: state.voidY - 5,
      vx,
      vy,
      radius: MORTAR.projectileRadius,
      color: 0xff00ff,
      trail: [],
      owner: -1,
      weapon: MORTAR,
      bouncesRemaining: 0,
      behaviorState: {},
      ignoreTerrain: false,
      ignoreTanks: false,
      ignoreWind: false,
      spawnFrame: state.frameCount,
      generation: 0,
    };
    state.projectiles.push(voidProj);

    // Per-orb particle burst
    fx.push({ type: 'particles', method: 'spawnVoidLaunchBurst', args: [orb.x, state.voidY, voidColor] });
    fx.push({ type: 'void_edge_distortion', x: orb.x, y: state.voidY });
  }

  // Single set of screen effects for the whole volley (scale with count)
  const shakeScale = Math.min(count, 5);
  fx.push({ type: 'camera_shake', intensity: 12 + shakeScale * 3, decay: 0.85 });
  fx.push({ type: 'screen_shake', intensity: 8 + shakeScale * 2, decay: 0.85 });
  fx.push({ type: 'screen_flash', color: 0xff00ff, alpha: 0.08 + count * 0.02 });

  // Camera follows average position
  const avgX = readyOrbs.reduce((sum, o) => sum + o.x, 0) / count;
  fx.push({ type: 'camera_follow', x: avgX, y: state.voidY - 20 });

  // Retaliation personality text for big volleys (3+)
  if (count >= 3) {
    const line = getRetaliationLine();
    fx.push({
      type: 'announce_world',
      text: line,
      x: avgX,
      y: state.voidY - 50,
      color: VOID_PERSONALITY.TEXT_COLOR,
      fontSize: 36,
    });
  }

  if (state.phase !== 'firing') transitionPhase(state, 'firing', fx);

  return fx;
}
